import { isObject } from 'lodash';
import { checkCurrency, roundOff, staticDefaultCostingInfoColumnArray } from '../../../../Components/helper';
import type { HeaderStat } from '../../../../Components/BombiixAGGrid';
import type { LandedInfoEntry, NamedRef } from './store/Costing.type';
import i18next from 'i18next';

/** One of the ten fixed default Costing Information fields, as stored on a costing template. */
export interface DefaultCostingInfoField {
  title?: string;
  aliasDisplayName?: string;
  position?: number;
}

/** A costing's populated Sample Ref; `label` is the server-built "<Sample Type> - <Sample Number>". */
export interface SampleRefDisplay {
  sampleNumber?: string;
  label?: string;
}

/**
 * Resolves a default column's header label from a template's Costing Information overrides.
 *
 * Keyed on the canonical slot TITLE, never the stored `position`. `position` is a user-controlled
 * DISPLAY ORDER, not a slot identity: dragging a row in the template builder's Costing Information
 * table renumbers every default by its new on-screen order
 * (`CostingInfoTable.handleRowDragEnd`), and that handler also drops any row without an `_id`
 * before numbering, shifting everything after it. Matching on position therefore slid each label
 * along by one on any template that had ever been reordered — the Currency column rendered slot 3's
 * title, "Costing Template", and MOQ Needed rendered "Currency".
 *
 * Default titles are immutable in the template builder (a rename writes `aliasDisplayName` and
 * leaves `title` alone), so they are the only stable handle on a slot. This mirrors
 * `ProductInfoTable.getCostingInfoLabel` on the costing detail page, which already resolved it
 * this way.
 * @param defaultCostingInfo the template's `isDefault` costingInfo fields
 * @param slot 0-based index into the canonical default field order
 * @param fallback label to use when the field is absent or costing-info labelling is off
 * @param isCostingInfoActive whether the template enables costing-info labelling at all
 * @returns the label the column header should show
 */
export const resolveCostingInfoLabel = (
  defaultCostingInfo: DefaultCostingInfoField[] | undefined,
  slot: number,
  fallback: string,
  isCostingInfoActive: boolean,
): string => {
  if (!isCostingInfoActive) return fallback;
  const canonicalTitle = (staticDefaultCostingInfoColumnArray as DefaultCostingInfoField[])[slot]?.title;
  if (!canonicalTitle) return fallback;
  const field = (defaultCostingInfo ?? []).find((entry) => entry?.title === canonicalTitle);
  return field ? field.aliasDisplayName || field.title || fallback : fallback;
};

/* Pure formatters shared by the table component, the columns hook and the Excel export.
   Extracted verbatim from the JavaScript CostingTODOTable so the two consumers cannot drift. */

/** Resolves a currency code to its symbol, or an empty string when unknown. */
export const setCurrency = (currencyVal: string | undefined): string => {
  return checkCurrency(currencyVal, undefined, true) || '';
};

/** True when the value is absent or numerically zero. */
export const isZeroAmount = (value: unknown): boolean => Number(value ?? 0) === 0;

/**
 * Renders a money cell. Parent rows carrying more than one expanded entry show 'Multiple'
 * rather than a figure, and an exact zero renders as a bare '0' with no currency symbol.
 */
export const formatCurrencyValue = (currencyVal: string, value: unknown, hasExpanded = false): string => {
  if (hasExpanded) return 'Multiple';
  if (isZeroAmount(value)) return '0';
  return `${currencyVal}${value}`.trim();
};

/** Renders a percentage cell, collapsing zero to a bare '0'. */
export const formatPercentageValue = (value: unknown): string => (isZeroAmount(value) ? '0' : `${value} %`);

/**
 * Rounds a stats figure for a floating header badge. Returns null for zero or NaN so the
 * caller omits the badge entirely.
 */
export const getFloatingSum = (rawValue: unknown, decimals = 2): string | number | null => {
  const rounded = roundOff(rawValue ?? 0, decimals, true);
  return Number(rounded) ? rounded : null;
};

/**
 * Builds the grid's `headerStats` map — the aggregate chip shown above each column — from the
 * `/dashboard/costing-stats` payload.
 *
 * Keys are grid colIds, so the shared grid can hand each chip straight to its column's header. The
 * three dynamic families use the template's own alias as the stats key, which is why the template
 * column arrays are needed here rather than a fixed lookup table.
 *
 * A figure that rounds to zero yields no entry at all (`getFloatingSum` returns null), so an
 * unavailable stat leaves a plain header rather than an empty chip.
 *
 * @param stats the sums/averages block from the costing-stats endpoint
 * @param columns the template's active column families plus the user's chosen static columns
 * @returns colId → chip, ready to pass to BombiixAGGrid's `headerStats`
 */
export const buildCostingHeaderStats = (
  stats: { sums?: Record<string, number | undefined>; averages?: Record<string, number | undefined> } | null | undefined,
  columns: {
    selectedColumns?: string[];
    topColumnsArr?: Array<{ aliasName?: string }> | undefined;
    defaultColumnsArr?: Array<{ aliasName?: string }> | undefined;
    bottomColumnsArr?: Array<{ aliasName?: string; isActive?: boolean }> | undefined;
  },
): Record<string, HeaderStat> => {
  const headerStats: Record<string, HeaderStat> = {};
  if (!stats) return headerStats;
  const { selectedColumns, topColumnsArr, defaultColumnsArr, bottomColumnsArr } = columns;

  const addSum = (colId: string, statKey: string) => {
    const value = getFloatingSum(stats.sums?.[statKey]);
    if (value !== null) headerStats[colId] = { value, tone: 'sum' };
  };

  if (selectedColumns?.includes('quantity')) addSum('quantity', 'quantityPerSizCol');
  if (selectedColumns?.includes('totalIncoterm')) addSum('totalIncoterm', 'totalInco');

  (topColumnsArr ?? []).forEach((column) => {
    if (column?.aliasName) addSum(`top_${column.aliasName}`, column.aliasName);
  });

  (defaultColumnsArr ?? []).forEach((column) => {
    if (column?.aliasName) addSum(`default_${column.aliasName}`, column.aliasName);
  });

  /* Bottom columns prefer the average — the margin ratios are only meaningful averaged — and fall
     back to the sum for the money columns in the same family. */
  (bottomColumnsArr ?? []).forEach((column) => {
    const alias = column?.aliasName;
    if (!alias || column?.isActive === false) return;
    const average = getFloatingSum(stats.averages?.[alias]);
    if (average !== null) {
      headerStats[`bottom_${alias}`] = { value: `${average}%`, tone: 'average' };
      return;
    }
    addSum(`bottom_${alias}`, alias);
  });

  return headerStats;
};

/** Unwraps a populated `{ name }` reference, falling back to '--' for empty scalars. */
/** localStorage key holding the user's chosen Costing TODO columns. */
export const COSTING_TODO_COLUMNS_KEY = 'CostingToDoColumns';

/**
 * Reads the saved column selection, carrying columns added since the user last chose.
 *
 * The selection is stored verbatim, so a column introduced later stays invisible until the user
 * re-opens the chooser. `Price Per Unit` was split out of `Price Per Unit (Incoterms)` — the latter
 * used to be the editable cell — so anyone who had the Incoterms column selected is given the
 * editable one beside it rather than silently losing the ability to edit a price. Freight Automated
 * replaces Freight Value / Freight % on templates that switch to it, so anyone who kept a freight column
 * is given it beside that column rather than losing the freight figure from the grid and the export.
 * @returns the stored options, with the new columns inserted where they belong
 */
export const readSelectedColumnOptions = (): Array<{ label?: string; value?: string }> => {
  let saved: Array<{ label?: string; value?: string }>;
  try {
    const parsed = JSON.parse(localStorage.getItem(COSTING_TODO_COLUMNS_KEY) || '[]');
    saved = Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
  if (saved.length === 0) return saved;

  const values = new Set(saved.map((option) => option?.value));
  if (values.has('pricePerUnitIncoterm') && !values.has('pricePerUnit')) {
    const at = saved.findIndex((option) => option?.value === 'pricePerUnitIncoterm');
    saved.splice(at, 0, { label: 'Price Per Unit', value: 'pricePerUnit' });
  }
  const savedFreightAt = saved.findIndex((option) => option?.value === 'freightValue' || option?.value === 'freightPercentage');
  if (savedFreightAt !== -1 && !values.has('freightAutomated') && !hasFreightAutomatedBeenOffered()) {
    saved.splice(savedFreightAt + 1, 0, { label: String(i18next.t('COSTING_TODO.FREIGHT_AUTOMATED', { ns: 'costing' })), value: 'freightAutomated' });
    rememberFreightAutomatedOffered(saved);
  }
  return saved;
};

export const getValue = (value: string | number | NamedRef | null | undefined): string | number => {
  if (isObject(value)) {
    return (value as NamedRef)?.name || '';
  }
  /* lodash's isObject is untyped here (declarations.d.ts declares the module bare), so it does
     not narrow. The cast is safe because the branch above consumed every object case. */
  return (value as string | number | undefined) || '--';
};

/**
 * Reads one landed-info figure. Percentage rows carry the display figure on `calculatedValue`;
 * every other key reads `tempValue`.
 */
export const getLandedValue = (data: LandedInfoEntry[] | undefined, id: string | undefined, keyType: string | undefined): string | number | undefined => {
  if (keyType === 'percentage') {
    return data?.find((item) => item?._id === id && item?.key === keyType)?.calculatedValue;
  }
  return data?.find((item) => item?._id === id && item?.key === keyType)?.tempValue;
};

/** localStorage flag set once Freight Automated has been added to a saved selection. */
const FREIGHT_AUTOMATED_OFFERED_KEY = 'CostingToDoColumnsFreightAutomatedOffered';

/**
 * Whether Freight Automated has already been added to this browser's saved selection, so a user who
 * then hides it in the chooser keeps it hidden instead of having it re-added on every read.
 * @returns True once it has been offered, or when storage cannot be read.
 */
function hasFreightAutomatedBeenOffered(): boolean {
  try {
    return localStorage.getItem(FREIGHT_AUTOMATED_OFFERED_KEY) === '1';
  } catch {
    return true;
  }
}

/**
 * Saves the selection with Freight Automated added and marks it offered, so the column stays put
 * until the user changes the selection themselves.
 * @param selection The selection with Freight Automated added.
 * @returns True when it was saved, false when storage is unavailable.
 */
function rememberFreightAutomatedOffered(selection: Array<{ label?: string; value?: string }>): boolean {
  try {
    localStorage.setItem(COSTING_TODO_COLUMNS_KEY, JSON.stringify(selection));
    localStorage.setItem(FREIGHT_AUTOMATED_OFFERED_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * The text a saved Sample Ref is shown with. Costing reads send the populated sample carrying its
 * `label`; Range Plan reads send the label as a plain string.
 * @param sampleRef the costing's `sampleRef` as received
 * @returns the label, else the bare sample number, else empty
 */
export const getSampleRefLabel = (sampleRef: SampleRefDisplay | string | null | undefined): string => {
  if (typeof sampleRef === 'string') return sampleRef;
  return sampleRef?.label || sampleRef?.sampleNumber || '';
};
